import './App.scss'
// @deno-types="@types/react"
import React from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { CameraProvider } from "./features/camera/CameraContext.tsx";
import SharedCameraLayout from "./features/camera/SharedCameraLayout.tsx";
import ConsumeFeedContent from "./features/consume/ConsumeFeedContent.tsx";
import PurchaseFeedContent from "./features/purchase/PurchaseFeedContent.tsx";
import GroceryList from "./features/grocery/GroceryList.tsx";
import RecipePage from "./features/recipes/RecipePage.tsx";
import AIRecipeDetailPage from "./features/recipes/AIRecipeDetailPage.tsx";
import LoginPage from "./features/login/LoginPage.tsx";
import SettingsPage from "./features/settings/SettingsPage.tsx";
import ProtectedRoute from "./features/camera/ProtectedRoute.tsx";
import { storage } from "utils/storage.ts";
import QueryBoundary from "components/QueryBoundaries.tsx";

const PurchaseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const ConsumeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/>
    <line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/>
    <line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
);

const RecipesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
  </svg>
);

const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const CameraRouteShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <CameraProvider autoStart>
      <SharedCameraLayout>{children}</SharedCameraLayout>
    </CameraProvider>
  </ProtectedRoute>
);

const App: React.FC = () => {
  const location = useLocation();
  const hasConfig = storage.hasConfig();

  return (
    <div className="app">
      <main className="app__main">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/consume" element={
            <CameraRouteShell>
              <QueryBoundary fallback={<div className="camera-feed"><div className="camera-feed__loading">Loading stock data...</div></div>}>
                <ConsumeFeedContent />
              </QueryBoundary>
            </CameraRouteShell>
          } />
          <Route path="/purchase" element={
            <CameraRouteShell>
              <QueryBoundary fallback={<div className="camera-feed"><div className="camera-feed__loading">Loading data...</div></div>}>
                <PurchaseFeedContent />
              </QueryBoundary>
            </CameraRouteShell>
          } />
          <Route path="/list" element={
            <ProtectedRoute>
              <QueryBoundary fallback={<div className="grocery-list"><div className="grocery-list__content"><div className="grocery-list__loading"><p>Loading your grocery list...</p></div></div></div>}>
                <GroceryList />
              </QueryBoundary>
            </ProtectedRoute>
          } />
          <Route path="/recipes" element={
            <ProtectedRoute>
              <QueryBoundary fallback={<div className="recipe-page__loading"><div className="recipe-page__loading-spinner"><span className="loading-spinner-ring" /></div></div>}>
                <RecipePage />
              </QueryBoundary>
            </ProtectedRoute>
          } />
          <Route path="/recipes/ai-detail" element={
            <ProtectedRoute>
              <QueryBoundary fallback={<div style={{ padding: 16, display: 'flex', justifyContent: 'center' }}><span className="loading-spinner-ring" /></div>}>
                <AIRecipeDetailPage />
              </QueryBoundary>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <QueryBoundary fallback={<div style={{ padding: 16, display: 'flex', justifyContent: 'center' }}><span className="loading-spinner-ring" /></div>}>
                <SettingsPage />
              </QueryBoundary>
            </ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to={hasConfig ? "/consume" : "/login"} replace />} />
          <Route path="*" element={<Navigate to={hasConfig ? "/consume" : "/login"} replace />} />
        </Routes>
      </main>

      {/* Navigation */}
      {hasConfig && (
        <nav className="app__navigation">
          <Link
            to="/purchase"
            className={`nav-button ${location.pathname === '/purchase' ? 'nav-button--active' : ''}`}
          >
            <span className="nav-button__icon"><PurchaseIcon /></span>
            <span className="nav-button__text">Purchase</span>
          </Link>
          <Link
            to="/consume"
            className={`nav-button ${location.pathname === '/consume' ? 'nav-button--active' : ''}`}
          >
            <span className="nav-button__icon"><ConsumeIcon /></span>
            <span className="nav-button__text">Consume</span>
          </Link>
          <Link
            to="/list"
            className={`nav-button ${location.pathname === '/list' ? 'nav-button--active' : ''}`}
          >
            <span className="nav-button__icon"><ListIcon /></span>
            <span className="nav-button__text">List</span>
          </Link>
          <Link
            to="/recipes"
            className={`nav-button ${location.pathname === '/recipes' ? 'nav-button--active' : ''}`}
          >
            <span className="nav-button__icon"><RecipesIcon /></span>
            <span className="nav-button__text">Recipes</span>
          </Link>
          <Link
            to="/settings"
            className={`nav-button ${location.pathname === '/settings' ? 'nav-button--active' : ''}`}
          >
            <span className="nav-button__icon"><SettingsIcon /></span>
            <span className="nav-button__text">Settings</span>
          </Link>
        </nav>
      )}
    </div>
  );
};

export default App;
